import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canAccess, hasPermission } from '../../rbac';
import { propertyAPI } from '../../services/api';
import {
  OccupancyForm,
  PropertyActionDialog,
  ServiceConnectionForm,
  ValuationForm,
} from './PropertyWorkspaceForms';
import PropertyInterestChangeRequests from './PropertyInterestChangeRequests';
import { LocationMap } from '../../components/maps/LocationMap';
import {
  apiErrorMessage,
  currentInterests,
  formatCurrency,
  formatNumber,
  fullAddress,
  INTEREST_OPTIONS,
  isConflict,
  labelFor,
  OCCUPANCY_OPTIONS,
  PROPERTY_KIND_OPTIONS,
  PROPERTY_STATUS_OPTIONS,
  SERVICE_TYPE_OPTIONS,
  statusClass,
  USAGE_OPTIONS,
  VALUATION_SOURCE_OPTIONS,
  VERIFICATION_OPTIONS,
  workflowStatusLabel,
} from './propertyRegistry';
import './AdminProperties.css';

const displayDate = value => value ? new Intl.DateTimeFormat('mr-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(`${value}T00:00:00`)) : '—';

const DetailItem = ({ label, children }) => <div><dt>{label}</dt><dd>{children ?? '—'}</dd></div>;

export default function AdminPropertyWorkspace() {
  const { tenantSlug, propertyUid } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const canView = canAccess(user?.role, 'properties', user);
  const canUpdate = hasPermission(user, 'properties', 'update');
  const canVerify = hasPermission(user, 'properties', 'verify');
  const canArchive = hasPermission(user, 'properties', 'archive');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(location.state?.message || '');
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);
  const [action, setAction] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await propertyAPI.getByUid(tenantSlug, propertyUid);
      setDetail(response.data);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'मालमत्ता तपशील लोड करता आला नाही.'));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView, propertyUid, tenantSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!message) return undefined;
    const timer = window.setTimeout(() => setMessage(''), 6000);
    return () => window.clearTimeout(timer);
  }, [message]);

  const property = detail?.property;
  const archived = property?.status === 'archived';
  const activeOwners = useMemo(() => currentInterests(detail?.interests), [detail]);
  const historicalOwners = useMemo(() => (detail?.interests || []).filter(item => item.status !== 'active'), [detail]);
  const pendingTaxValuation = useMemo(() => (detail?.valuations || [])
    .filter(item => item.status === 'pending_approval')
    .sort((left, right) => String(right.effective_from || '').localeCompare(String(left.effective_from || '')))[0] || null, [detail]);
  const approvedTaxValuation = useMemo(() => (detail?.valuations || []).find(item => {
    if (item.status !== 'approved') return false;
    const currentDate = new Date().toISOString().slice(0, 10);
    return item.effective_from <= currentDate && (!item.effective_to || item.effective_to >= currentDate);
  }) || null, [detail]);
  const approvedValuationComplete = Boolean(approvedTaxValuation
    && Number(approvedTaxValuation.assessed_value) > 0
    && Number(approvedTaxValuation.annual_rental_value) > 0
    && (property?.property_kind === 'parcel'
      ? Number(approvedTaxValuation.land_value) > 0
      : Number(approvedTaxValuation.building_value) > 0));
  const valuationBlocksVerification = Boolean(
    property?.property_tax_applicable && (pendingTaxValuation || !approvedValuationComplete)
  );

  const saved = (nextDetail, successMessage) => {
    setDetail(nextDetail);
    setEditor(null);
    setError('');
    setMessage(successMessage);
  };

  const executeAction = async reason => {
    if (!action || !property || busy) return;
    setBusy(true);
    setError('');
    try {
      let response;
      const payload = { version: property.version, ...(reason ? { reason } : {}) };
      if (action.kind === 'submit') response = await propertyAPI.submit(tenantSlug, propertyUid, payload);
      if (action.kind === 'verify') response = await propertyAPI.verify(tenantSlug, propertyUid, payload);
      if (action.kind === 'reject') response = await propertyAPI.reject(tenantSlug, propertyUid, payload);
      if (action.kind === 'archive') response = await propertyAPI.archive(tenantSlug, propertyUid, payload);
      if (action.kind === 'valuation-approve') response = await propertyAPI.approveValuation(tenantSlug, propertyUid, action.record.valuation_uid, payload);
      if (action.kind === 'valuation-reject') response = await propertyAPI.rejectValuation(tenantSlug, propertyUid, action.record.valuation_uid, payload);
      setDetail(response.data);
      setMessage(action.success);
      setAction(null);
      setEditor(null);
    } catch (requestError) {
      const suffix = isConflict(requestError) ? ' पुन्हा प्रयत्न करण्यापूर्वी नवीनतम आवृत्ती लोड करा.' : '';
      setError(`${apiErrorMessage(requestError, 'कृती पूर्ण करता आली नाही.')}${suffix}`);
      setAction(null);
    } finally {
      setBusy(false);
    }
  };

  const [downloading8A, setDownloading8A] = useState(false);

  const handleDownloadNamuna8 = async () => {
    try {
      setDownloading8A(true);
      const res = await propertyAPI.downloadNamuna8(tenantSlug, propertyUid);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(apiErrorMessage(err, 'नमुना ८ (8A) उतारा तयार करताना त्रुटी आली.'));
    } finally {
      setDownloading8A(false);
    }
  };

  if (!canView) return <section className="property-permission-card" role="alert"><h1>मालमत्ता नोंदवही</h1><p>तुमच्याकडे ही नोंद पाहण्याची परवानगी नाही.</p></section>;
  if (loading) return <div className="property-page-loader" role="status">मालमत्ता लोड होत आहे…</div>;
  if (!property) return <div className="property-registry-page"><div className="property-alert error" role="alert"><p>{error || 'मालमत्ता सापडली नाही.'}</p><button type="button" onClick={load}>पुन्हा प्रयत्न करा</button></div></div>;

  const actionDetails = action ? {
    submit: ['पडताळणीसाठी पाठवा', 'मालकीचा हिस्सा आणि प्राथमिक करदायी मालक तपासून नोंद पडताळणीसाठी पाठवली जाईल.', 'पाठवा', false],
    verify: ['मालमत्ता पडताळा', 'ही नोंद अधिकृत कर व प्रशासकीय वापरासाठी पडताळलेली म्हणून चिन्हांकित होईल. तपासणीची नोंद द्या.', 'पडताळा', true],
    reject: ['नोंद नामंजूर करा', 'दुरुस्तीसाठी स्पष्ट कारण द्या. मालमत्ता पुन्हा संपादित व सादर करता येईल.', 'नामंजूर करा', true],
    archive: ['मालमत्ता संग्रहित करा', 'मालकी, वास्तव्यास व सेवा जोडण्या बंद होतील. ही नोंद हटवली जाणार नाही.', 'संग्रहित करा', true],
    'valuation-approve': ['मूल्यांकन मंजूर करा', 'याच कालावधीतील जुने मंजूर मूल्यांकन आपोआप पूर्व आवृत्ती होईल. तपासणीची नोंद द्या.', 'मंजूर करा', true],
    'valuation-reject': ['मूल्यांकन नामंजूर करा', 'मूल्यांकन करणाऱ्यासाठी स्पष्ट कारण द्या.', 'नामंजूर करा', true],
  }[action.kind] : null;

  return (
    <div className="property-registry-page property-workspace-page">
      <header className="property-workspace-header">
        <div>
        <Link className="property-back-link" to={`/admin/${tenantSlug}/properties`}>← मालमत्ता नोंदवही</Link>
          <div className="property-title-row">
            <span className="property-title-icon" aria-hidden="true">{property.property_kind === 'parcel' ? '▱' : property.property_kind === 'unit' ? '▦' : '⌂'}</span>
            <div>
              <p className="property-eyebrow">{labelFor(PROPERTY_KIND_OPTIONS, property.property_kind)}</p>
              <h1>{property.property_name}</h1>
              <p>{property.assessment_number} · {fullAddress(property)}</p>
            </div>
          </div>
        </div>
        <div className="property-header-actions">
          <div className="property-status-stack">
            <span className={`property-status ${statusClass(property.verification_status)}`}>{labelFor(VERIFICATION_OPTIONS, property.verification_status)}</span>
            <small>आवृत्ती {property.version}</small>
          </div>
          <button
            type="button"
            className="property-secondary-button"
            onClick={handleDownloadNamuna8}
            disabled={downloading8A}
            title="नमुना ८ (8A) कर आकारणी नोंदवही उतारा डाउनलोड / प्रिंट करा"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontWeight: 600,
              color: '#1e3a8a',
              backgroundColor: '#eff6ff',
              borderColor: '#bfdbfe',
            }}
          >
            {downloading8A ? 'तयार होत आहे…' : '📄 नमुना ८ (8A)'}
          </button>
          {canUpdate && !archived && <Link className="property-secondary-button" to={`/admin/${tenantSlug}/properties/${propertyUid}/edit`}>संपादन</Link>}
          {canUpdate && ['draft', 'rejected'].includes(property.verification_status) && !archived && <button type="button" className="property-primary-button" onClick={() => setAction({ kind: 'submit', success: 'मालमत्ता पडताळणीसाठी पाठवली.' })}>पडताळणीसाठी पाठवा</button>}
          {canVerify && property.verification_status === 'pending' && !valuationBlocksVerification && <button type="button" className="property-primary-button success" onClick={() => setAction({ kind: 'verify', success: 'मालमत्ता पडताळली.' })}>पडताळा</button>}
          {canVerify && property.verification_status === 'pending' && valuationBlocksVerification && pendingTaxValuation && <button type="button" className="property-primary-button success" onClick={() => setAction({ kind: 'valuation-approve', record: pendingTaxValuation, success: 'मूल्यांकन मंजूर केले. आता मालमत्ता पडताळा.' })}>प्रथम मूल्यांकन मंजूर करा</button>}
          {canVerify && property.verification_status === 'pending' && <button type="button" className="property-secondary-button danger" onClick={() => setAction({ kind: 'reject', success: 'नोंद नामंजूर केली.' })}>नामंजूर करा</button>}
          {canArchive && !archived && <button type="button" className="property-text-button danger" onClick={() => setAction({ kind: 'archive', success: 'मालमत्ता संग्रहित केली.' })}>संग्रहित करा</button>}
        </div>
      </header>
      {property.verification_status === 'pending' && valuationBlocksVerification && (
        <div className="property-alert warning" role="status">
          <p>
            {pendingTaxValuation
              ? 'मालमत्ता कराची माहिती भरलेली आहे, परंतु मूल्यांकन अजून मंजुरी प्रलंबित आहे. प्रथम मूल्यांकन मंजूर करा; त्यानंतर मालमत्ता पडताळता येईल.'
              : approvedTaxValuation
                ? 'सध्याच्या मंजूर मूल्यांकनात कर गणनेसाठी आवश्यक रक्कम अपूर्ण आहे. मालमत्ता संपादित करून सुधारित मूल्यांकन मंजुरीसाठी पाठवा.'
                : 'मालमत्ता पडताळण्यापूर्वी कर विभागात मंजुरीसाठी मूल्यांकन नोंद तयार करणे आवश्यक आहे.'}
          </p>
          {!pendingTaxValuation && canUpdate && <Link to={`/admin/${tenantSlug}/properties/${propertyUid}/edit`}>मालमत्ता व मूल्यांकन दुरुस्त करा</Link>}
        </div>
      )}

      {message && <div className="property-alert success" role="status">{message}</div>}
      {error && <div className="property-alert error" role="alert"><p>{error}</p><button type="button" onClick={load}>नवीनतम आवृत्ती लोड करा</button></div>}
      {property.metadata?.verification_rejection?.reason && property.verification_status === 'rejected' && <div className="property-alert warning"><strong>नामंजुरीचे कारण:</strong> {property.metadata.verification_rejection.reason}</div>}
      {property.metadata?.auto_generated && (
        <div className="property-alert warning" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e' }}>
          <div>
            <strong>ℹ️ कुटुंबावरून तयार केलेला मसुदा (Auto-generated Draft):</strong>
            <p style={{ margin: '0.35rem 0 0 0', fontSize: '0.9rem' }}>
              ही मालमत्ता कुटुंब नोंदीवरून स्वयंचलितपणे तयार करण्यात आली आहे. कर आकारणीसाठी खालील तपशील तपासा व पूर्ण करा:
              {property.land_area_sqft == null && ' • क्षेत्रफळ (Land/Built-up Area)'}
              {(!property.valuations || property.valuations.length === 0) && ' • चालू वार्षिक मूल्यांकन (Valuation)'}
              {!property.property_number && ' • मिळकत क्रमांक (Property Number)'}
              {!property.survey_number && !property.gat_number && ' • गट/सर्व्हे क्रमांक (Gat/Survey Number)'}
            </p>
          </div>
          {canUpdate && !archived && (
            <Link className="property-primary-button" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }} to={`/admin/${tenantSlug}/properties/${propertyUid}/edit`}>
              तपशील पूर्ण करा
            </Link>
          )}
        </div>
      )}
      {archived && <div className="property-alert neutral">ही मालमत्ता संग्रहित आहे आणि फक्त पाहता येते.</div>}

      <nav className="property-section-nav" aria-label="मालमत्ता कार्यक्षेत्र विभाग">
        <a href="#property-overview">सारांश</a><a href="#property-owners">मालकी</a><a href="#property-owner-changes">बदल</a><a href="#property-occupancy">वास्तव्य</a><a href="#property-valuations">मूल्यांकन</a><a href="#property-services">सेवा</a>
      </nav>

      <section id="property-overview" className="property-workspace-card">
        <div className="property-card-heading"><div><p className="property-eyebrow">अधिकृत नोंद</p><h2>मालमत्ता सारांश</h2></div></div>
        <dl className="property-detail-grid">
          <DetailItem label="मालमत्ता UID"><span className="property-monospace">{property.property_uid}</span></DetailItem>
          <DetailItem label="मालमत्तेचे नाव">{property.property_name}</DetailItem>
          <DetailItem label="मिळकत क्रमांक">{property.assessment_number}</DetailItem>
          <DetailItem label="नोंद स्थिती"><span className={`property-status ${statusClass(property.status)}`}>{labelFor(PROPERTY_STATUS_OPTIONS, property.status)}</span></DetailItem>
          <DetailItem label="वापर">{labelFor(USAGE_OPTIONS, property.usage_type)}</DetailItem>
          <DetailItem label="बांधकाम प्रकार">{property.construction_type || '—'}</DetailItem>
          <DetailItem label="सर्वे क्रमांक">{property.survey_number || '—'}</DetailItem>
          <DetailItem label="गट क्रमांक">{property.gat_number || '—'}</DetailItem>
          <DetailItem label="प्लॉट क्रमांक">{property.plot_number || '—'}</DetailItem>
          <DetailItem label="प्रभाग">{property.ward_number || '—'}</DetailItem>
          <DetailItem label="जमीन क्षेत्र">{formatNumber(property.land_area_sqft)} चौ. फूट</DetailItem>
          <DetailItem label="बांधकाम क्षेत्र">{formatNumber(property.built_up_area_sqft)} चौ. फूट</DetailItem>
          <DetailItem label="कार्पेट क्षेत्र">{formatNumber(property.carpet_area_sqft)} चौ. फूट</DetailItem>
          <DetailItem label="मजला">{[property.floor_number, property.total_floors ? `एकूण ${property.total_floors} पैकी` : null].filter(Boolean).join(' ') || '—'}</DetailItem>
          <DetailItem label="बांधकाम वर्ष">{property.construction_year || '—'}</DetailItem>
          <DetailItem label="पालक मालमत्ता">{property.parent_property_uid ? <Link to={`/admin/${tenantSlug}/properties/${property.parent_property_uid}`}>{property.parent_property_uid}</Link> : '—'}</DetailItem>
          <DetailItem label="शेवटचा बदल">{property.updated_at ? new Date(property.updated_at).toLocaleString('mr-IN') : '—'}</DetailItem>
        </dl>
        <LocationMap latitude={property.latitude} longitude={property.longitude} title={property.property_name} address={fullAddress(property)} />
      </section>

      <section id="property-owners" className="property-workspace-card">
        <div className="property-card-heading"><div><p className="property-eyebrow">करदायित्व</p><h2>मालकी हक्क</h2></div>{canUpdate && !archived && <Link className="property-secondary-button" to={`/admin/${tenantSlug}/properties/${propertyUid}/edit`}>{property.verification_status === 'verified' ? 'बदल प्रस्तावित करा' : 'मालकी बदला'}</Link>}</div>
        <div className="property-summary-strip"><span><strong>{activeOwners.reduce((sum, item) => sum + Number(item.ownership_share || 0), 0).toFixed(4).replace(/\.0+$/, '')}%</strong> चालू मालकी</span><span><strong>{activeOwners.filter(item => item.is_primary && item.is_liable_party).length}</strong> प्राथमिक करदायी</span></div>
        {activeOwners.length === 0 ? <div className="property-empty-inline">चालू मालकी नोंद नाही.</div> : <div className="property-record-list">{activeOwners.map(item => <article key={item.id} className="property-record-row"><div className="property-record-main"><strong>{item.party?.display_name || item.party?.legal_name || 'मालकाचे नाव उपलब्ध नाही'}</strong><small>{item.party?.primary_phone || item.party?.party_uid}</small></div><div><span>{labelFor(INTEREST_OPTIONS, item.interest_type)}</span><small>{item.ownership_share ?? '—'}% हिस्सा</small></div><div><span>{displayDate(item.valid_from)} – {displayDate(item.valid_to)}</span><small>{item.is_primary ? 'प्राथमिक' : ''} {item.is_liable_party ? '· करदायी' : ''}</small></div></article>)}</div>}
        {historicalOwners.length > 0 && <details className="property-history"><summary>मागील मालकी नोंदी ({historicalOwners.length})</summary><div className="property-record-list">{historicalOwners.map(item => <article key={item.id} className="property-record-row muted"><div className="property-record-main"><strong>{item.party?.display_name || item.party?.legal_name || 'मालकाचे नाव उपलब्ध नाही'}</strong><small>{workflowStatusLabel(item.status)}</small></div><div>{labelFor(INTEREST_OPTIONS, item.interest_type)}</div><div>{displayDate(item.valid_from)} – {displayDate(item.valid_to)}</div></article>)}</div></details>}
      </section>

      <section id="property-owner-changes" className="property-workspace-card">
        <div className="property-card-heading">
          <div><p className="property-eyebrow">प्रस्तावक–पडताळणी नियंत्रण</p><h2>मालकी बदल विनंत्या</h2></div>
          <p className="property-heading-note">प्रत्येक विनंतीमध्ये आधीची नोंद आणि प्रस्तावित संपूर्ण मालकी कायम जतन केली जाते.</p>
        </div>
        <PropertyInterestChangeRequests
          tenantSlug={tenantSlug}
          propertyUid={propertyUid}
          user={user}
          canVerify={canVerify}
          onPropertyChanged={() => load({ silent: true })}
        />
      </section>

      <section id="property-occupancy" className="property-workspace-card">
        <div className="property-card-heading"><div><p className="property-eyebrow">वास्तव्य करणारा</p><h2>वास्तव्य इतिहास</h2></div>{canUpdate && !archived && !editor && <button type="button" className="property-secondary-button" onClick={() => setEditor({ type: 'occupancy', record: null })}>＋ वास्तव्यास जोडा</button>}</div>
        {editor?.type === 'occupancy' && <OccupancyForm tenantSlug={tenantSlug} propertyUid={propertyUid} version={property.version} record={editor.record} onCancel={() => setEditor(null)} onSaved={saved} />}
        {(detail.occupancies || []).length === 0 ? <div className="property-empty-inline">वास्तव्य नोंद नाही.</div> : <div className="property-record-list">{detail.occupancies.map(item => <article key={item.id} className="property-record-row"><div className="property-record-main"><strong>{labelFor(OCCUPANCY_OPTIONS, item.occupancy_type)}</strong><small>{item.party?.display_name || item.party?.legal_name || (item.household_id ? `कुटुंब क्र. ${item.household_id}` : 'रहिवासी नोंद उपलब्ध नाही')}</small></div><div><span className={`property-status ${statusClass(item.status)}`}>{workflowStatusLabel(item.status)}</span><small>{item.is_primary ? 'प्राथमिक वास्तव्यास' : 'दुय्यम वास्तव्यास'}</small></div><div><span>{displayDate(item.valid_from)} – {displayDate(item.valid_to)}</span>{canUpdate && !archived && ['pending', 'active'].includes(item.status) && <button type="button" className="property-table-link" onClick={() => setEditor({ type: 'occupancy', record: item })}>संपादन</button>}</div></article>)}</div>}
      </section>

      <section id="property-valuations" className="property-workspace-card">
        <div className="property-card-heading"><div><p className="property-eyebrow">कराचा आधार</p><h2>मूल्यांकन</h2></div>{canUpdate && !archived && !editor && <button type="button" className="property-secondary-button" onClick={() => setEditor({ type: 'valuation', record: null })}>＋ मूल्यांकन जोडा</button>}</div>
        {editor?.type === 'valuation' && <ValuationForm tenantSlug={tenantSlug} propertyUid={propertyUid} version={property.version} record={editor.record} onCancel={() => setEditor(null)} onSaved={saved} />}
        {(detail.valuations || []).length === 0 ? <div className="property-empty-inline">मूल्यांकन नोंद नाही.</div> : <div className="property-valuation-grid">{detail.valuations.map(item => <article key={item.valuation_uid} className="property-valuation-card"><header><div><strong>{formatCurrency(item.assessed_value ?? item.building_value ?? item.land_value)}</strong><small>{labelFor(VALUATION_SOURCE_OPTIONS, item.source)}</small></div><span className={`property-status ${statusClass(item.status)}`}>{workflowStatusLabel(item.status)}</span></header><dl><DetailItem label="लागू कालावधी">{displayDate(item.effective_from)} – {displayDate(item.effective_to)}</DetailItem><DetailItem label="जमीन मूल्य">{formatCurrency(item.land_value)}</DetailItem><DetailItem label="इमारत मूल्य">{formatCurrency(item.building_value)}</DetailItem><DetailItem label="वार्षिक भाडेमूल्य">{formatCurrency(item.annual_rental_value)}</DetailItem></dl>{item.notes && <p>{item.notes}</p>}<footer>{canUpdate && !archived && ['draft', 'pending_approval', 'rejected'].includes(item.status) && <button type="button" className="property-table-link" onClick={() => setEditor({ type: 'valuation', record: item })}>संपादन</button>}{canVerify && !archived && item.status === 'pending_approval' && <><button type="button" className="property-table-link success" onClick={() => setAction({ kind: 'valuation-approve', record: item, success: 'मूल्यांकन मंजूर केले.' })}>मंजूर करा</button><button type="button" className="property-table-link danger" onClick={() => setAction({ kind: 'valuation-reject', record: item, success: 'मूल्यांकन नामंजूर केले.' })}>नामंजूर करा</button></>}</footer></article>)}</div>}
      </section>

      <section id="property-services" className="property-workspace-card">
        <div className="property-card-heading"><div><p className="property-eyebrow">जोडण्या</p><h2>सेवा जोडण्या</h2></div>{canUpdate && !archived && !editor && <button type="button" className="property-secondary-button" onClick={() => setEditor({ type: 'service', record: null })}>＋ सेवा जोडा</button>}</div>
        {editor?.type === 'service' && <ServiceConnectionForm tenantSlug={tenantSlug} propertyUid={propertyUid} version={property.version} record={editor.record} onCancel={() => setEditor(null)} onSaved={saved} />}
        {(detail.service_connections || []).length === 0 ? <div className="property-empty-inline">सेवा जोडणी नाही.</div> : <div className="property-service-grid">{detail.service_connections.map(item => <article key={item.connection_uid} className="property-service-card"><span className="property-service-icon" aria-hidden="true">{item.service_type === 'water' ? '◉' : '◆'}</span><div><strong>{labelFor(SERVICE_TYPE_OPTIONS, item.service_type)}</strong><small>{item.connection_number}</small><p>{[item.tariff_class, item.meter_number && `मीटर ${item.meter_number}`].filter(Boolean).join(' · ') || 'दरवर्ग किंवा मीटर तपशील उपलब्ध नाही'}</p></div><div><span className={`property-status ${statusClass(item.status)}`}>{workflowStatusLabel(item.status)}</span>{canUpdate && !archived && item.status !== 'archived' && <button type="button" className="property-table-link" onClick={() => setEditor({ type: 'service', record: item })}>संपादन</button>}</div></article>)}</div>}
      </section>

      {actionDetails && <PropertyActionDialog title={actionDetails[0]} description={actionDetails[1]} confirmLabel={actionDetails[2]} reasonRequired={actionDetails[3]} busy={busy} onCancel={() => setAction(null)} onConfirm={executeAction} />}
    </div>
  );
}
